<template>
  <div class="artwork-panel">
    <h5 class="artwork-panel-title">{{ title }}</h5>

    <div class="row">
      <!-- Geração -->
      <div class="col-lg-8 mb-3">
        <div class="card h-100 mb-0">
          <div class="card-body">
            <label class="d-flex align-items-center mb-2">
              <strong>Prompt</strong>
              <ArtworkPromptHelp />
            </label>
            <b-form-textarea
              :value="prompt"
              rows="5"
              :disabled="generating || promptLoading"
              placeholder="Carregando prompt..."
              @input="$emit('update:prompt', $event)"
            />
            <!-- Arte base: referência de estilo para a IA criar uma arte nova parecida -->
            <div class="d-flex align-items-center flex-wrap mt-3">
              <strong class="mr-2">Arte base <small class="text-muted">(opcional)</small></strong>
              <input
                ref="baseFile"
                type="file"
                accept="image/png,image/jpeg,image/webp"
                class="d-none"
                @change="onBaseFile"
              />
              <template v-if="baseFile">
                <img :src="basePreview" alt="Arte base" class="base-thumb mr-2" />
                <span class="text-muted small text-truncate base-name mr-1">{{ baseFile.name }}</span>
                <button class="btn btn-link btn-sm p-0" :disabled="generating" @click="removeBase">
                  remover
                </button>
              </template>
              <button
                v-else
                class="btn btn-secondary btn-sm"
                :disabled="generating"
                @click="$refs.baseFile.click()"
              >
                <i class="fa fa-paperclip mr-1"></i> Anexar arte base
              </button>
            </div>
            <ArtworkLogoPicker
              class="mt-3 mb-0"
              :value="logoCodes"
              :disabled="generating"
              @input="$emit('update:logoCodes', $event)"
            />
            <div class="d-flex justify-content-end flex-wrap mt-3">
              <input ref="file" type="file" accept="image/png,image/jpeg" class="d-none" @change="onFile" />
              <button
                class="btn btn-secondary mr-2 mb-1"
                :disabled="uploading || generating"
                @click="$refs.file.click()"
              >
                <i class="fa mr-1" :class="uploading ? 'fa-spinner fa-spin' : 'fa-upload'"></i>
                Enviar arte pronta
              </button>
              <button
                class="btn btn-success mb-1"
                :disabled="generating || !(prompt || '').trim()"
                @click="$emit('generate', baseFile)"
              >
                <i class="fa mr-1" :class="generating ? 'fa-spinner fa-spin' : 'fa-magic'"></i>
                {{ generating ? "Gerando..." : "Gerar com IA" }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- QR Code -->
      <div class="col-lg-4 mb-3">
        <div class="card h-100 mb-0">
          <!-- Mesma estrutura vertical do card do prompt: título alinhado ao "Prompt", QR ocupa
               o espaço da textarea + logos, botão alinhado aos botões de geração. -->
          <div class="card-body d-flex flex-column">
            <label class="d-flex align-items-center mb-2">
              <strong>{{ qrTitle }}</strong>
            </label>
            <div class="qr-area">
              <RotateSquare v-if="qrLoading" size="30px" />
              <img v-else-if="qrSrc" :src="qrSrc" alt="QR Code" />
              <span v-else class="text-muted">QR Code indisponível</span>
            </div>
            <div class="d-flex justify-content-center mt-3">
              <button class="btn btn-secondary mb-1" :disabled="!qrSrc" @click="$emit('download-qr')">
                <i class="fa fa-download mr-1"></i> Baixar QR
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Artes geradas / enviadas -->
    <template v-if="artworks.length">
      <strong class="d-block mb-2">Artes ({{ artworks.length }})</strong>
      <div class="row">
        <div v-for="(a, i) in artworks" :key="a.id" class="col-6 col-md-4 col-xl-3 mb-3">
          <div class="card artwork-card h-100 mb-0" :class="{ latest: i === 0 }">
            <a
              :href="a.file_url || null"
              target="_blank"
              rel="noopener"
              class="artwork-thumb"
              :title="formatDate(a.generated_at)"
            >
              <img v-if="a.file_url" :src="a.file_url" alt="Arte" />
              <i v-else class="fa fa-image fa-2x text-muted"></i>
              <span v-if="i === 0" class="badge badge-success latest-badge">Mais recente</span>
            </a>
            <div class="card-footer p-2 d-flex">
              <button
                class="btn btn-secondary btn-sm mr-1 flex-grow-1"
                :disabled="!a.file_url"
                title="Baixar"
                @click="$emit('download', a)"
              >
                <i class="fa fa-download"></i>
              </button>
              <button class="btn btn-danger btn-sm flex-grow-1" title="Excluir" @click="$emit('delete', a)">
                <i class="fa fa-trash"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import RotateSquare from "./RotateSquare";
import ArtworkLogoPicker from "./ArtworkLogoPicker";
import ArtworkPromptHelp from "./ArtworkPromptHelp";

const BASE_MAX_MB = 20;

// Seção de arte de divulgação (prompt + arte base + logos + QR + galeria). Só apresentação: o
// estado e as chamadas de API ficam na tela (diagnóstico, workshop, audiência pública); a arte
// base anexada (referência de estilo) vai no evento `generate`.
export default {
  name: "ArtworkPanel",
  components: { RotateSquare, ArtworkLogoPicker, ArtworkPromptHelp },
  props: {
    title: { type: String, required: true },
    qrTitle: { type: String, default: "QR Code" },
    qrSrc: { type: String, default: null },
    qrLoading: { type: Boolean, default: false },
    prompt: { type: String, default: "" },
    promptLoading: { type: Boolean, default: false },
    generating: { type: Boolean, default: false },
    logoCodes: { type: Array, default: () => [] },
    artworks: { type: Array, default: () => [] },
    uploading: { type: Boolean, default: false },
  },
  data() {
    return { baseFile: null, basePreview: null };
  },
  beforeDestroy() {
    if (this.basePreview) URL.revokeObjectURL(this.basePreview);
  },
  methods: {
    onBaseFile(e) {
      const file = e.target.files && e.target.files[0];
      e.target.value = "";
      if (!file) return;
      if (file.size > BASE_MAX_MB * 1024 * 1024) {
        this.$notify({ data: [`A arte base deve ter no máximo ${BASE_MAX_MB} MB.`], type: "warn" });
        return;
      }
      this.baseFile = file;
      if (this.basePreview) URL.revokeObjectURL(this.basePreview);
      this.basePreview = URL.createObjectURL(file);
    },
    removeBase() {
      URL.revokeObjectURL(this.basePreview);
      this.baseFile = null;
      this.basePreview = null;
    },
    onFile(e) {
      const file = e.target.files && e.target.files[0];
      e.target.value = "";
      if (file) this.$emit("upload", file);
    },
    formatDate(value) {
      return value ? new Date(value).toLocaleString("pt-BR") : "";
    },
  },
};
</script>

<style scoped>
.artwork-panel-title {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
}
/* Ocupa a altura que sobra no card (definida pelo card do prompt, ao lado) sem aumentá-la:
   o conteúdo é absoluto, então o QR não influencia a altura da linha. */
.qr-area {
  position: relative;
  flex: 1 1 auto;
  min-height: 160px;
}
.qr-area > * {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
.qr-area img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.base-thumb {
  height: 40px;
  width: auto;
  border-radius: 4px;
  border: 1px solid #d8dbe0;
}
.base-name {
  max-width: 180px;
}
.artwork-card.latest {
  border-color: #2eb85c;
}
.artwork-thumb {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: 2 / 3;
  overflow: hidden;
  background: #f4f5f7;
}
.artwork-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.latest-badge {
  position: absolute;
  top: 6px;
  left: 6px;
}
</style>
